import React, { useState, useEffect } from 'react';
import { Mail, Lock, Calendar, Users, FileText, Clock, TrendingUp, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { login, clearAuthData, isAuthenticated } from '../utils/auth';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      console.log('User already authenticated, redirecting to dashboard');
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      // Clear any existing auth data first
      clearAuthData();

      // Login using auth utility
      const data = await login(formData.email, formData.password);

      console.log('✅ Login successful:', {
        user: data.user.name,
        role: data.user.role,
        email: data.user.email
      });

      // Redirect to home/dashboard
      navigate('/');
      // window.location.reload(); // Not needed in SPA usually
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Generic statistics
  const systemStats = {
    totalEmails: 1247,
    todayInterviews: 8,
    upcomingInterviews: 23,
    activeCandidates: 156,
    openPositions: 12,
    lastUpdated: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-gray-50 via-slate-50 to-cyan-50/40">
      {/* LEFT SIDE - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative overflow-hidden bg-gradient-to-br from-white via-gray-50/50 to-cyan-50/60">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(8, 145, 178, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(8, 145, 178, 0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-cyan-200/40 to-blue-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-1/2 right-1/3 w-80 h-80 bg-gradient-to-br from-blue-200/40 to-indigo-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/2 w-72 h-72 bg-gradient-to-br from-cyan-100/30 to-sky-100/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative w-full max-w-2xl px-4">
          <div className="bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] border-l-4 border-t-4 border-cyan-500 rounded-tr-[2.5rem] rounded-bl-[2.5rem] rounded-tl-lg rounded-br-lg overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500"></div>
            <div className="px-12 py-10">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-6">
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontSize: '42px',
                    fontWeight: '500',
                    position: 'relative',
                  }}>
                    {/* Circular Arrow Icon forming "O" */}
                    <div style={{
                      width: '42px',
                      height: '42px',
                      position: 'relative',
                      flexShrink: 0,
                      marginRight: '4px',
                    }}>
                      <svg viewBox="0 0 50 50" style={{
                        width: '100%',
                        height: '100%',
                        filter: 'drop-shadow(0 4px 8px rgba(0, 188, 212, 0.4))',
                      }}>
                        <defs>
                          <linearGradient id="loginLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style={{ stopColor: '#00BCD4', stopOpacity: 1 }} />
                            <stop offset="50%" style={{ stopColor: '#0891B2', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: '#2563EB', stopOpacity: 1 }} />
                          </linearGradient>
                        </defs>
                        <g>
                          <path
                            fill="url(#loginLogoGradient)"
                            stroke="url(#loginLogoGradient)"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="
                            M 11.2,22.6
                            L 3.3,21.2
                            A 22 22 0 0 1 44.1,14.0
                            L 47.5,12.0
                            L 42.7,21.9
                            L 33.7,20.0
                            L 37.1,18.0
                            A 14 14 0 0 0 11.2,22.6
                            Z" />

                          <path
                            fill="url(#loginLogoGradient)"
                            stroke="url(#loginLogoGradient)"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="
                            M 38.8,27.4
                            L 46.7,28.8
                            A 22 22 0 0 1 6.0,36.0
                            L 2.5,38.0
                            L 7.3,28.1
                            L 16.3,30.0
                            L 12.9,32.0
                            A 14 14 0 0 0 38.8,27.4
                            Z" />
                        </g>
                      </svg>
                    </div>

                    {/* Text part "rdinaa" */}
                    <span style={{
                      display: 'inline-block',
                      whiteSpace: 'nowrap',
                      background: 'linear-gradient(135deg, #00BCD4 0%, #0891B2 50%, #2563EB 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}>rdinaa</span>
                  </div>
                </div>
                <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 via-cyan-500 to-blue-600 mb-2">
                  Ordinaa Workspace
                </h1>
                <p className="text-gray-600 text-sm font-medium">
                  Access your AI tools
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-cyan-500 transition-colors" size={20} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-all bg-white/70 hover:bg-white"
                    />
                  </div>
                </div>

                <div className="relative group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-cyan-500 transition-colors" size={20} />
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-all bg-white/70 hover:bg-white"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">⚠</span>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-3.5 rounded-xl font-medium hover:from-cyan-700 hover:to-blue-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-cyan-300 shadow-lg hover:shadow-xl ${
                    loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - System Overview */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-600 p-8 flex-col justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 w-full text-white">
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-3">System Overview</h2>
            <p className="text-cyan-50 text-sm">Real-time insights</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 text-gray-800 shadow-xl">
               <Mail className="text-cyan-600 mb-2" size={24} />
               <h3 className="text-sm font-medium text-gray-500">Emails</h3>
               <p className="text-2xl font-bold">{systemStats.totalEmails}</p>
            </div>
             <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 text-gray-800 shadow-xl">
               <Calendar className="text-blue-600 mb-2" size={24} />
               <h3 className="text-sm font-medium text-gray-500">Today</h3>
               <p className="text-2xl font-bold">{systemStats.todayInterviews}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
